// Global axios interceptor — silently refreshes the access cookie on 401
// and replays the original request once. This is what prevents the
// "screen frozen" symptom doctors saw after stepping away for 30+ min:
// before this, an expired access cookie caused every API call to 401 and
// React silently swallowed the rejection.
//
// Mounted once in App.js (see import side-effect there). Idempotent — a
// module-level flag prevents double-registration during HMR.
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
let _installed = false;
let _refreshInFlight = null; // shared promise so concurrent 401s coalesce

export function installAuthInterceptor() {
  if (_installed) return;
  _installed = true;

  axios.defaults.withCredentials = true;

  // Request interceptor: stamp every outbound call with the current tenant
  // slug from localStorage. This is what tells the backend tenancy
  // middleware which database / tenant_id to scope this request to.
  axios.interceptors.request.use((config) => {
    try {
      const t = window.localStorage?.getItem('tenantSlug');
      if (t && !config.headers?.['X-Tenant-Slug']) {
        config.headers = { ...(config.headers || {}), 'X-Tenant-Slug': t };
      }
    } catch (_) { /* ignore */ }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;

      // Bail conditions:
      //  - no request object to retry (e.g. CORS preflight)
      //  - already retried once (avoid infinite loop)
      //  - the failing request IS the refresh endpoint
      //  - the failing request is a login (we DO want login errors to surface)
      if (
        !original ||
        original._authRetry ||
        original.url?.includes('/api/auth/refresh') ||
        original.url?.includes('/api/auth/login') ||
        original.url?.includes('/api/auth/doctor-login') ||
        original.url?.includes('/api/auth/staff-login') ||
        // `GET /api/auth/me` is fired on every page mount to detect a
        // logged-in session. In incognito / on public marketing pages
        // (/c/<slug>, /signup, /) this legitimately 401s — treat that
        // as "signed out", not as an auth error worth refreshing +
        // redirecting for. Prior behaviour bounced anonymous visitors
        // from the public clinic landing page to the login screen.
        original.url?.includes('/api/auth/me')
      ) {
        return Promise.reject(error);
      }
      if (status !== 401) return Promise.reject(error);

      try {
        // Coalesce parallel 401s into a single refresh call. Every retry
        // awaits the same promise, then replays its own request.
        if (!_refreshInFlight) {
          _refreshInFlight = axios
            .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
            .finally(() => {
              // Tiny tick so dependent retries see the resolved/rejected
              // state before the slot reopens.
              setTimeout(() => { _refreshInFlight = null; }, 0);
            });
        }
        await _refreshInFlight;
      } catch (_) {
        // Refresh itself failed (refresh cookie expired / invalid). Bounce
        // the user to the right login page based on their current route.
        _redirectToLogin();
        return Promise.reject(error);
      }

      original._authRetry = true;
      original.withCredentials = true;
      return axios(original);
    }
  );
}

function _redirectToLogin() {
  // Avoid redirect loops if we're already on a login page.
  const path = window.location.pathname || '';
  if (/^\/(?:staff|pharmacy|purchase)?\/?login/.test(path) || path === '/login') return;
  // Super-admin console has its own auth realm and login flow — never
  // bounce those pages to the tenant login. Same for the public signup.
  if (path.startsWith('/superadmin') || path.startsWith('/signup')) return;
  // Public marketing / booking surfaces — the clinic landing page
  // (/c/<slug>) and the root landing — MUST NOT bounce anonymous
  // visitors to a login screen. A public visitor tapping "Book" or
  // just landing on the page is not an authentication failure.
  if (path === '/' || path.startsWith('/c/') || path.startsWith('/book/')) return;
  // Pick the login page that matches the current portal.
  let dest = '/login';
  if (path.startsWith('/pharmacy-stores') || path.startsWith('/stores-admin')) dest = '/staff/login';
  else if (path.startsWith('/pharmacy')) dest = '/pharmacy/login';
  else if (path.startsWith('/purchase')) dest = '/purchase/login';
  // Preserve `?tenant=<slug>` (and any other query) so a 401-bounce keeps
  // the user inside their tenant. Falls back to localStorage if the
  // current URL doesn't carry the slug — never strip it silently.
  let qs = window.location.search || '';
  if (!qs.includes('tenant=')) {
    try {
      const t = window.localStorage?.getItem('tenantSlug');
      if (t) qs = qs ? `${qs}&tenant=${encodeURIComponent(t)}` : `?tenant=${encodeURIComponent(t)}`;
    } catch (_) { /* noop */ }
  }
  // Hard reload so any in-memory React state is wiped — leaving stale
  // patient context on a re-login screen is a privacy footgun.
  window.location.assign(`${dest}${qs}`);
}
